<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, nextTick, useTemplateRef } from 'vue'
import ExamCard from '@/components/Head/ExamCard.vue'
import type { Study } from '@pocketprep/types'
import type { ClientExam } from '@/store/bundle/state'
import { subscriptionModule } from '@/store/subscription/module'
import type { ComponentPublicInstance } from 'vue'

const props = defineProps<{
    examMetadata: (Study.Class.ExamMetadataJSON | ClientExam)[]
    currentExamMetadata: Study.Class.ExamMetadataJSON | ClientExam
    focusFirstCard: boolean
}>()

const emit = defineEmits<{
    close: []
    addExam: []
    clickExamCard: [exam: Study.Class.ExamMetadataJSON | ClientExam]
}>()

const currentFocusIndex = ref(0)

const examCards = useTemplateRef<ComponentPublicInstance[]>('examCards')
const examCardAdd = useTemplateRef<HTMLElement>('examCard-add')

const getExamCardElFromIndex = (index: number) => {
    const examCardRefs = examCards.value || []
    const examCardRef: ComponentPublicInstance | undefined = examCardRefs[index]
    return examCardRef?.$el as HTMLElement | undefined
}

onMounted(() => {
    // close side panel on `esc` press
    window.addEventListener('keydown', keydownListener, true)

    if (props.focusFirstCard) {
        nextTick(() => {
            const firstCardEl = getExamCardElFromIndex(0)
            firstCardEl?.focus()
        })
    }
})

onBeforeUnmount(() => {
    window.removeEventListener('keydown', keydownListener, true)
})

const clickedExamCard = (exam: Study.Class.ExamMetadataJSON | ClientExam) => {
    emit('clickExamCard', exam)
}

const keydownListener = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
        e.stopPropagation()
        emit('close')
    }
    if (e.key === 'ArrowDown') {
        e.preventDefault()
        if (currentFocusIndex.value < (props.examMetadata.length - 1)) {
            // If not yet at the end, move one item down the list
            getExamCardElFromIndex(currentFocusIndex.value + 1)?.focus()
            currentFocusIndex.value++
        } else if (currentFocusIndex.value === (props.examMetadata.length - 1)) {
            // If on the last exam, move to the Add Exam button
            examCardAdd.value?.focus()
            currentFocusIndex.value++
        } else {
            // If on the last exam button, move back to the top
            getExamCardElFromIndex(0)?.focus()
            currentFocusIndex.value = 0
        }
    }
    if (e.key === 'ArrowUp') {
        e.preventDefault()
        if (currentFocusIndex.value === props.examMetadata.length) {
            // If on the Add Exam button, move to the last exam
            getExamCardElFromIndex(props.examMetadata.length - 1)?.focus()
            currentFocusIndex.value = props.examMetadata.length - 1
        } else if (currentFocusIndex.value > 0) {
            // If not already at the top, move one item up the list
            getExamCardElFromIndex(currentFocusIndex.value - 1)?.focus()
            currentFocusIndex.value--
        } else {
            // If at the top, move back to the Add Exam button
            examCardAdd.value?.focus()
            currentFocusIndex.value = props.examMetadata.length
        }
    }
}

const isExamPremium = (examId: string) => {
    return !!subscriptionModule.getters.getSubscriptionForExamId(examId)
}

const emitAddExam = () => {
    emit('addExam')
}
</script>

<template>
    <div v-dark class="exam-menu-dropdown">
        <ExamCard
            v-for="(exam, index) in examMetadata"
            :key="JSON.stringify(exam)"
            ref="examCards"
            :is-premium="isExamPremium(exam.objectId)"
            :exam="exam"
            :show-menu-arrow="index === 0"
            menu-arrow-direction="up"
            theme="grayscale"
            :role="index === 0 ? 'button' : false"
            :aria-expanded="index === 0 ? 'true' : false"
            :is-focusable="true"
            @click="clickedExamCard(exam)"
            @keydown.enter="clickedExamCard(exam)"
            @mousedown.prevent
        />
        <div
            ref="examCard-add"
            v-dark
            tabindex="0"
            class="exam-menu-dropdown__add"
            @click="emitAddExam"
            @keydown.enter="emitAddExam"
            @mousedown.prevent
        >
            + Add Exam
        </div>
    </div>
</template>

<style lang="scss" scoped>
.exam-menu-dropdown {
    border-radius: 4px;
    position: absolute;
    max-height: calc(100vh - 20px);
    overflow-y: auto;
    z-index: 1;
    left: 0;
    top: 0;
    border: 1px solid $gray-divider;
    background: $white;
    box-shadow: 0 6px 22px 0 rgba($brand-black, 0.2);
    width: 320px;

    @include breakpoint(black-bear) {
        width: calc(100% + 52px);
        top: 1px;
    }

    & > div {
        border-bottom: 1px solid $gray-divider;

        &:last-child {
            border-bottom: 0;
        }
    }

    &--dark {
        background: $charcoal;
        border-color: rgba($fog, 0.25);
        box-shadow: 0 6px 22px 0 rgba($jet, 0.6);

        & > div {
            border-bottom-color: rgba($fog, 0.25);
        }
    }

    &__add {
        color: $brand-blue;
        font-size: 14px;
        line-height: 19px;
        letter-spacing: 0.32px;
        font-weight: 600;
        padding: 9px 8px;
        cursor: pointer;
        user-select: none;
        outline: none;
        position: relative;

        &:hover {
            background: $gray-background;
        }

        &:focus::before {
            content: '';
            left: 0;
            top: 0;
            position: absolute;
            width: calc(100% - 2px);
            height: calc(100% - 2px);
            border-radius: 6px;
            border: 1px solid $brand-blue;
        }

        &--dark {
            color: $banana-bread;

            &:focus::before {
                border-color: $banana-bread;
            }

            &:hover {
                background: $brand-black;
            }
        }
    }
}
</style>
